import { motion } from "framer-motion";
import { Target, Megaphone, Inbox, MessagesSquare, Building2, Globe2, ArrowUpRight } from "lucide-react";
import { Overline } from "./Mark";
import { Reveal } from "./Reveal";
import { scrollToId } from "@/lib/lenis";

const SERVICES = [
  { icon: Target, title: "Real Estate Lead Generation", text: "Online campaigns designed to attract prospective buyers and interested clients for selected property projects.", span: "md:col-span-2 lg:col-span-7", image: "/img/commercial.jpg" },
  { icon: Megaphone, title: "Property Marketing & Promotion", text: "Digital advertising and promotion that puts property opportunities in front of the right audience.", span: "lg:col-span-5" },
  { icon: Inbox, title: "Property Enquiry Management", text: "Website and campaign enquiries captured, organised and routed with care.", span: "lg:col-span-4" },
  { icon: MessagesSquare, title: "Client Follow-Up & Communication", text: "Professional, timely follow-up so every enquiry receives a clear response.", span: "lg:col-span-4" },
  { icon: Building2, title: "Residential & Commercial Property Enquiries", text: "Support across both residential homes and commercial real-estate opportunities.", span: "md:col-span-2 lg:col-span-4" },
  { icon: Globe2, title: "US, UK & Netherlands Property Project Marketing", text: "Marketing support for selected property projects and developments across our international markets.", span: "md:col-span-2 lg:col-span-12", wide: true },
];

const onMove = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const Card = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      onMouseMove={onMove}
      className={`spot-card group relative flex min-h-[280px] flex-col overflow-hidden rounded-[28px] border border-[rgba(212,175,55,0.16)] bg-[#0F1E36]/70 p-7 transition-[border-color] duration-500 hover:border-[rgba(212,175,55,0.45)] sm:p-9 ${s.span}`}
      data-testid={`service-card-${i + 1}`}
    >
      {s.image && (
        <div className="absolute inset-0 -z-10">
          <img src={s.image} alt="" className="h-full w-full object-cover opacity-30 transition-[transform,opacity] duration-[1.2s] group-hover:scale-105 group-hover:opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0F1E36] via-[#0F1E36]/85 to-transparent" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <span className="grid h-12 w-12 place-items-center rounded-2xl border border-[rgba(212,175,55,0.3)] text-[#D4AF37] transition-[background-color,color] duration-500 group-hover:bg-[#D4AF37] group-hover:text-[#070E17]"><Icon className="h-5 w-5" /></span>
        <span className="font-mono-d text-xs tracking-[0.2em] text-slate-400">0{i + 1} / 06</span>
      </div>
      <div className={`mt-auto pt-12 ${s.wide ? "lg:flex lg:items-end lg:justify-between lg:gap-12" : ""}`}>
        <div>
          <h3 className={`font-serif-d font-medium leading-tight text-white ${i === 0 || s.wide ? "text-3xl sm:text-4xl" : "text-2xl sm:text-[1.7rem]"}`}>{s.title}</h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">{s.text}</p>
        </div>
        {s.wide && (
          <div className="mt-8 flex flex-wrap gap-3 lg:mt-0">
            {["United States", "United Kingdom", "Netherlands"].map((m) => (
              <span key={m} className="rounded-full border border-[rgba(212,175,55,0.3)] px-4 py-2 text-sm text-[#E6C280]">{m}</span>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="relative bg-[#070E17] py-24 lg:py-36" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <Overline>Our Services — 06</Overline>
          <h2 className="font-serif-d mt-6 text-4xl font-medium leading-[1.02] text-white sm:text-5xl lg:text-6xl">Marketing that turns interest into <em className="gold-text italic">real enquiries.</em></h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-slate-300">We support real-estate marketing campaigns, generate property enquiries and help prospective clients explore residential and commercial opportunities.</p>
          <button onClick={() => scrollToId("contact")} className="group mt-6 inline-flex items-center gap-2 text-sm text-[#E6C280]" data-testid="services-enquire-link">
            Discuss your project <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </button>
        </Reveal>
      </div>
      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12">
        {SERVICES.map((s, i) => <Card key={s.title} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
